const ipv6Regex =
  /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|(::)?([0-9a-fA-F]{1,4}:){6}([0-9a-fA-F]{1,4}|::)|([0-9a-fA-F]{1,4}:){5}(:[0-9a-fA-F]{1,4}){1,2}|(::)?([0-9a-fA-F]{1,4}:){4}(:[0-9a-fA-F]{1,4}){1,3}|(::)?([0-9a-fA-F]{1,4}:){3}(:[0-9a-fA-F]{1,4}){1,4}|(::)?([0-9a-fA-F]{1,4}:){2}(:[0-9a-fA-F]{1,4}){1,5}|(::)?([0-9a-fA-F]{1,4}:){1}(:[0-9a-fA-F]{1,4}){1,6}|(::):([0-9a-fA-F]{1,4}){1,7}$/;
const ipv4Regex = /^((25[0-5]|(2[0-4]|1\d{0,2})?\d{0,2})\.){3}(25[0-5]|(2[0-4]|1\d{0,2})?\d{0,2})$/;
const dmarcRegex = /^(v=DMARC\s*)(.*)/i;
const domainValidationRegex =
  /^(?:[_a-z0-9](?:[_a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?)?$/i;
export const SPF_RECORD_PREFIX = "v=spf1";

export const isValidIpv4 = ip => {
  return new RegExp(ipv4Regex).test(ip);
};

export const isValidIpv6 = ip => {
  return new RegExp(ipv6Regex).test(ip);
};

export const isValidIP = ip => {
  return isValidIpv4(ip) || isValidIpv6(ip);
};

export const isDkimRecord = val => {
  return val && val.toLowerCase().startsWith("v=dkim1") && val.toLowerCase().includes("p=");
};

export const isSpfRecord = val => {
  return val && val.toLowerCase().startsWith("v=spf1");
};

export const isDmarcRecord = val => {
  return new RegExp(dmarcRegex).test(val);
};

export const isValidDomain = val => {
  return new RegExp(domainValidationRegex).test(val);
};

/**
 *
 * @param {{spfVal: String, tokenSets: Set, spfSuffix: String}} param0
 * @returns {{tokenSets: Set, spfSuffix: String}}
 */
export const generateSpfRecordTokens = ({ spfVal, tokenSets, spfSuffix }) => {
  spfVal.split(" ").map(word => {
    // Exclude if token has "v=spf1"
    if (!word) {
      return;
    }
    if (word.includes(SPF_RECORD_PREFIX)) {
      return;
    }
    // If token contains all, store it to spfSuffix
    if (word.includes("all")) {
      spfSuffix = word;
      return;
    }
    tokenSets.add(word);
    return word;
  });

  return { tokenSets, spfSuffix };
};

/**
 *
 * @param {String} prevSpfVal Previous SPF record value
 * @param {String} currSpfVal Current SPF record value
 * @return  {String} Merged SPF record value
 * e.g. prevSpfVal = "v=spf1 include:spf.ops.flock-staging.com ~all"
 * currSpfVal = "v=spf1 include:mandrillapp.com ~all"
 * Output = "v=spf1 include:spf.ops.flock-staging.com include:mandrillapp.com ~all"
 */
export const mergeSpfRecord = (prevSpfVal, currSpfVal) => {
  // Create Object which stores set and spfSuffix
  let spfRecord = {
    tokenSets: new Set(),
    spfSuffix: "+all", // It is the default prefix when no qualifier is used. It refers to Pass. The server with a matching IP address or domain is allowed to send for the domain.
  };

  // Generate set of tokens with prevSpfVal
  spfRecord = generateSpfRecordTokens({ spfVal: prevSpfVal, ...spfRecord });
  // Generate set of tokens with currSpfVal
  spfRecord = generateSpfRecordTokens({ spfVal: currSpfVal, ...spfRecord });

  // We are using set here, this will make only one entry in set for same multiple tokens

  let spfValue = SPF_RECORD_PREFIX;

  // Traverse the set and pick tokens one by one, and append it to spfValue
  for (const value of spfRecord?.tokenSets.values()) {
    spfValue += ` ${value}`;
  }

  // Final spfValue
  return spfValue + " " + spfRecord?.spfSuffix;
};

/**
 *
 * @param {String} currentSpf
 * @param {String} filteredSpf
 * @returns {String} Filtered SPF record
 * e.g. currentSPF = "v=spf1 include:spf.ops.flock-staging.com include:mail.zendesk.com include:mandrillapp.com ~all"
 * filteredSPF = "v=spf1 include:spf.ops.flock-staging.com ~all"
 * Output = "v=spf1  include:mail.zendesk.com include:mandrillapp.com ~all"
 */

export const filterSpfRecord = (currentSpf, filteredSpf) => {
  // Create currSpfRecord Object which stores set and spfSuffix
  let currSpfRecord = {
    tokenSets: new Set(),
    spfSuffix: "+all", // It is the default prefix when no qualifier is used. It refers to Pass. The server with a matching IP address or domain is allowed to send for the domain.
  };
  // Create filteredSpfRecord Object which stores set and spfSuffix
  let filteredSpfRecord = {
    tokenSets: new Set(),
    spfSuffix: "+all", // It is the default prefix when no qualifier is used. It refers to Pass. The server with a matching IP address or domain is allowed to send for the domain.
  };

  // Generate set of tokens for currendSpf
  currSpfRecord = generateSpfRecordTokens({
    spfVal: currentSpf,
    ...currSpfRecord,
  });

  // Generate set of tokens for filteredSpf
  filteredSpfRecord = generateSpfRecordTokens({
    spfVal: filteredSpf,
    ...filteredSpfRecord,
  });

  // Take the difference of set of currSpfRecord tokens with filteredSpfRecord tokens
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/difference
  currSpfRecord = {
    ...currSpfRecord,
    tokenSets: currSpfRecord.tokenSets?.difference(filteredSpfRecord.tokenSets),
  };

  let spfValue = SPF_RECORD_PREFIX;

  // Traverse the set and pick tokens one by one, and append it to spfValue
  for (const value of currSpfRecord?.tokenSets.values()) {
    spfValue += ` ${value}`;
  }

  // Final spfValue
  return spfValue + " " + currSpfRecord?.spfSuffix;
};

/**
 *
 * @param {String} spfVal
 * @returns {Boolean}
 */
export const isValidSpfRecord = spfVal => {
  return new RegExp(
    /^v=spf1( +([-+?~]?(all|include:(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*(\.([A-Za-z]|[A-Za-z]([-0-9A-Za-z]?)*[0-9A-Za-z])|%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\})|a(:(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*(\.([A-Za-z]|[A-Za-z]([-0-9A-Za-z]?)*[0-9A-Za-z])|%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}))?((\/(\d|1\d|2\d|3[0-2]))?(\/\/([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8]))?)?|mx(:(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*(\.([A-Za-z]|[A-Za-z]([-0-9A-Za-z]?)*[0-9A-Za-z])|%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}))?((\/(\d|1\d|2\d|3[0-2]))?(\/\/([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8]))?)?|ptr(:(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*(\.([A-Za-z]|[A-Za-z]([-0-9A-Za-z]?)*[0-9A-Za-z])|%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}))?|ip4:([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/([0-9]|1[0-9]|2[0-9]|3[0-2]))?|ip6:(::|([0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4}|([0-9A-Fa-f]{1,4}:){1,8}:|([0-9A-Fa-f]{1,4}:){7}:[0-9A-Fa-f]{1,4}|([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}){1,2}|([0-9A-Fa-f]{1,4}:){5}(:[0-9A-Fa-f]{1,4}){1,3}|([0-9A-Fa-f]{1,4}:){4}(:[0-9A-Fa-f]{1,4}){1,4}|([0-9A-Fa-f]{1,4}:){3}(:[0-9A-Fa-f]{1,4}){1,5}|([0-9A-Fa-f]{1,4}:){2}(:[0-9A-Fa-f]{1,4}){1,6}|[0-9A-Fa-f]{1,4}:(:[0-9A-Fa-f]{1,4}){1,7}|:(:[0-9A-Fa-f]{1,4}){1,8}|([0-9A-Fa-f]{1,4}:){6}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])|([0-9A-Fa-f]{1,4}:){6}:([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])|([0-9A-Fa-f]{1,4}:){5}:([0-9A-Fa-f]{1,4}:)?([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])|([0-9A-Fa-f]{1,4}:){4}:([0-9A-Fa-f]{1,4}:){0,2}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])|([0-9A-Fa-f]{1,4}:){3}:([0-9A-Fa-f]{1,4}:){0,3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])|([0-9A-Fa-f]{1,4}:){2}:([0-9A-Fa-f]{1,4}:){0,4}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])|[0-9A-Fa-f]{1,4}::([0-9A-Fa-f]{1,4}:){0,5}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])|::([0-9A-Fa-f]{1,4}:){0,6}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))(\/(\d{1,2}|10[0-9]|11[0-9]|12[0-8]))?|exists:(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*(\.([A-Za-z]|[A-Za-z]([-0-9A-Za-z]?)*[0-9A-Za-z])|%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}))|redirect=(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*(\.([A-Za-z]|[A-Za-z]([-0-9A-Za-z]?)*[0-9A-Za-z])|%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\})|exp=(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*(\.([A-Za-z]|[A-Za-z]([-0-9A-Za-z]?)*[0-9A-Za-z])|%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\})|[A-Za-z][-.0-9A-Z_a-z]*=(%\{[CDHILOPR-Tcdhilopr-t]([1-9][0-9]?|10[0-9]|11[0-9]|12[0-8])?r?[+-\/=_]*\}|%%|%_|%-|[!-$&-~])*))* *$/gm
  ).test(spfVal);
};

/**
 * function to transform bll expected records into render format
 * these records are used on domain connect screen to show as steps
 * @param {{ mx: [], spf: [], domainOwnershipTxt: [], a: []}} records
 * @param {string} domain
 */
export function parseDnsRecords(records, domain) {
  const mxItems = (records.mx || []).map((mxR, i) => ({
    Type: "MX",
    Host: "@",
    Point_to: mxR,
    Priority: 10 * (i + 1),
    "TTL (Seconds)": 3600,
  }));

  const spfItems = (records.spf || []).map(spfR => ({
    Type: "TXT",
    Host: "@",
    Point_to: spfR,
    "TTL (Seconds)": 3600,
  }));

  const domainOwnershipItems = (records.domainOwnershipTxt || []).map(domainOwnershipRecord => ({
    Type: "TXT",
    Host: "@",
    Point_to: domainOwnershipRecord,
    "TTL (Seconds)": 14400,
  }));

  const aItems = (records.a || []).map(a => ({
    Type: "A",
    Host: "@",
    Point_to: a,
  }));

  const cNameItems = [
    {
      Type: "CNAME",
      Host: "www",
      Point_to: domain,
    },
  ];

  return {
    mxItems,
    spfItems,
    domainOwnershipItems,
    aItems,
    cNameItems,
  };
}
